import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { Field } from '@shared/ui/primitives/Field';
import { Banner } from '@shared/ui/feedback/Banner';
import { CountryCodePicker, findCountry } from '@shared/ui/primitives/CountryCodePicker';
import { buildE164 } from '@shared/lib/phone';
import { useApiForm } from '@shared/hooks/useApiForm';
import { useModuleStore } from '@shared/stores/moduleStore';
import { ForgotSchema } from './schema';
import { pageCopy } from '@shared/lib/pageCopy';
import { forgotPassword } from './api';

/**
 * Forgot-password screen — honours the tenant's
 * `authConfig.allowed_identifiers` exactly like the sign-in screen does:
 *
 *   - `'email'`   → only the email field renders.
 *   - `'mobile'`  → only the mobile field (with country-code picker) renders.
 *   - `'both'`    → a small toggle lets the user pick a channel; the
 *                   matching field is shown.
 *
 * On submit the chosen `method` + `identifier` are sent to the server,
 * which selects the matching OTP template
 * (`authConfig.otp.templates.reset.email.template_id` vs `.sms.template_id`)
 * and delivers the 6-digit code over that channel. The user is then sent
 * to `/reset-password?method=…&identifier=…` to enter the OTP + their new
 * password.
 */
export function ForgotPassword(): JSX.Element {
  const authCopy = pageCopy().auth;
  const navigate = useNavigate();
  const authConfig = useModuleStore((s) => s.config?.authConfig);

  const allowedIdentifiers = authConfig?.allowed_identifiers ?? 'both';
  const canEmail = allowedIdentifiers !== 'mobile';
  const canMobile = allowedIdentifiers !== 'email';

  const [method, setMethod] = useState<'email' | 'mobile'>(canEmail ? 'email' : 'mobile');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('ZA');
  const [localMobile, setLocalMobile] = useState('');

  // If the config flips after first paint (e.g. domain-config loads
  // async), realign the active channel to whatever's allowed.
  useEffect(() => {
    if (method === 'email' && !canEmail) setMethod('mobile');
    if (method === 'mobile' && !canMobile) setMethod('email');
  }, [canEmail, canMobile, method]);

  const dialCode = findCountry(countryCode).dialCode;
  const mobileE164 = buildE164(dialCode, localMobile);
  const identifier = method === 'mobile' ? mobileE164 : email.trim();

  // The identifier + method live in local state (not RHF) so the channel
  // toggle can swap between email and country-picker+mobile inputs.
  // `useApiForm` is kept for the `run` / `serverMessage` / `isPending`
  // plumbing only — `handleSubmit` would gate on RHF's internal field
  // state (which we don't write to), so we bypass it and validate
  // against the Zod schema directly.
  const form = useApiForm({
    schema: ForgotSchema,
    defaultValues: { method, identifier: '' },
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.setServerMessage(null);
    const parsed = ForgotSchema.safeParse({ method, identifier });
    if (!parsed.success) {
      form.setServerMessage(parsed.error.issues[0]?.message ?? 'Check your details and try again.');
      return;
    }
    const ok = await form.run(() => forgotPassword(parsed.data));
    if (ok === true) {
      const qs = new URLSearchParams({ method, identifier });
      navigate(`/reset-password?${qs.toString()}`);
    }
  };

  return (
    <div className="flex min-h-full flex-col bg-paper px-6 pt-safe-or-6 pb-safe-or-6">
      <button
        type="button"
        onClick={() => navigate('/login')}
        aria-label="Back"
        className="flex h-9 w-9 items-center justify-center rounded-pill bg-sunken text-ink"
      >
        <ArrowLeft className="h-4.5 w-4.5" strokeWidth={1.75} />
      </button>

      <div className="mt-10 flex flex-1 flex-col">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
          {authCopy.forgotEyebrow}
        </span>
        <h1 className="display-serif mt-2 whitespace-pre-line text-display-lg leading-tight text-ink">
          {authCopy.forgotTitle}
        </h1>
        <p className="mt-3 max-w-sm text-body text-ink-secondary">{authCopy.forgotSubtitle}</p>

        {form.serverMessage ? (
          <Banner
            variant="danger"
            title="Couldn't send the code"
            description={form.serverMessage}
            onDismiss={() => form.setServerMessage(null)}
            className="mt-5 rounded-md"
          />
        ) : null}

        {/* Channel toggle — only renders when both channels are allowed. */}
        {canEmail && canMobile ? (
          <div
            className="mt-6 inline-flex self-start rounded-pill bg-sunken p-1 text-body-sm"
            role="tablist"
            aria-label="Send reset code by"
          >
            <button
              type="button"
              role="tab"
              aria-selected={method === 'email'}
              className={`rounded-pill px-3 py-1.5 font-medium transition-colors ${
                method === 'email' ? 'bg-paper text-ink shadow-sm' : 'text-ink-secondary'
              }`}
              onClick={() => setMethod('email')}
            >
              Email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={method === 'mobile'}
              className={`rounded-pill px-3 py-1.5 font-medium transition-colors ${
                method === 'mobile' ? 'bg-paper text-ink shadow-sm' : 'text-ink-secondary'
              }`}
              onClick={() => setMethod('mobile')}
            >
              Mobile
            </button>
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          {method === 'email' ? (
            <Field
              label="Registered email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-body-sm font-medium text-ink">Registered mobile</label>
              <div className="flex gap-2">
                <CountryCodePicker
                  value={countryCode}
                  onChange={(c) => setCountryCode(c.code)}
                />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="82 123 4567"
                  value={localMobile}
                  onChange={(event) => setLocalMobile(event.target.value.replace(/\D/g, '').slice(0, 15))}
                  className="h-12 w-full rounded-md border border-hairline-strong bg-elevated px-3.5 text-body text-ink placeholder:text-ink-muted focus:border-primary-600 focus:outline-none"
                />
              </div>
              <p className="text-caption text-ink-muted">
                We'll text the 6-digit code to {mobileE164 || '+…'}.
              </p>
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" width="full" loading={form.isPending}>
            Send reset code
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            width="full"
            onClick={() => navigate('/login')}
          >
            Back to sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
